import { useMergeState } from '@/shared/lib/customHooks';
import { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import type { MultiEmailInputProps } from './multiEmailInput.types';

/**
 * Backs the RMA notification multi-email input with its accepted email list and change handlers. Exposes setEmailList and getEmails through the forwarded ref, clears the list whenever the reset flag turns on, and on every list change reports it to onChangeEmailList while blanking the pending input text.
 *
 * @param onInputChangeProp - Parent callback receiving the pending input text.
 * @param onChangeEmailList - Parent callback receiving the accepted email list.
 * @param needToResetEmailMulti - Flag that empties the email list when set.
 * @param ref - Forwarded ref exposing setEmailList and getEmails.
 * @returns The email list state plus the email and input change handlers.
 */
const useMultiEmailInput = (
  {
    onInputChange: onInputChangeProp,
    onChangeEmailList,
    needToResetEmailMulti,
  }: MultiEmailInputProps,
  ref: any,
) => {
  const [state, setState] = useMergeState({
    emailList: [],
  });
  const onInputChangeRef = useRef(onInputChangeProp);
  onInputChangeRef.current = onInputChangeProp;

  useImperativeHandle(ref, () => ({
    setEmailList: (emailList: any) => setState({ emailList }),
    getEmails: () => state.emailList,
  }));

  //* Emails
  /**
   * Stores the addresses accepted by the multi-email input. Writes the incoming array into state as emailList, which triggers the effect that notifies the parent.
   *
   * @param data - The accepted email addresses.
   * @returns Nothing.
   */
  const onChangeEmails = (data: any) => {
    setState({ emailList: data });
  };

  /**
   * Forwards the multi-email field's pending text to the parent through the onInputChange prop. Wrapped in useCallback with an empty dependency list and reading the prop through a ref, so the handler keeps a stable identity across keystrokes.
   *
   * @param inputValue - The pending text typed into the inner field.
   * @returns Nothing.
   */
  const onChangeInputValue = useCallback((inputValue: any) => {
    onInputChangeRef.current(inputValue);
  }, []);

  useEffect(() => {
    if (needToResetEmailMulti) {
      setState({ emailList: [] });
    }
  }, [needToResetEmailMulti]);

  useEffect(() => {
    onChangeEmailList(state.emailList);
    onInputChangeProp('');
  }, [state.emailList]);

  return {
    state,
    onChangeEmails,
    onChangeInputValue,
  };
};

export { useMultiEmailInput };
